'use client';

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import { addDays, isSameLocalDay, startOfLocalDay, startOfWeek } from '@/lib/dates';
import { cn } from '@/lib/utils';

interface CalendarProps {
  /** Currently selected date (or null for no selection). */
  value: Date | null;
  onChange: (next: Date) => void;
  /** Optional minimum selectable date (inclusive). */
  minDate?: Date;
  className?: string;
}

const WEEKDAY = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'] as const;

// Custom Monday-first month calendar. Used standalone inside DatePicker /
// DateTimePicker — kept presentation-only so callers control state.
export function Calendar({ value, onChange, minDate, className }: CalendarProps) {
  // The visible month is independent of selection so navigating away then
  // back doesn't lose context.
  const today = useMemo(() => startOfLocalDay(new Date()), []);
  const [cursor, setCursor] = useState<Date>(
    value ? startOfMonth(value) : startOfMonth(today),
  );

  const monthLabel = cursor.toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });

  // Build 6 weeks of 7 days starting from the Monday on or before the 1st.
  const days = useMemo(() => {
    const firstOfMonth = startOfMonth(cursor);
    const gridStart = startOfWeek(firstOfMonth);
    return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  }, [cursor]);

  return (
    <div className={cn('w-[17rem] select-none', className)}>
      <header className="mb-2 flex items-center justify-between">
        <p className="eyebrow eyebrow-strong">{monthLabel}</p>
        <div className="flex items-center gap-0.5">
          <NavBtn label="Previous month" onClick={() => setCursor(addMonths(cursor, -1))}>
            <ChevronLeft className="h-3.5 w-3.5" strokeWidth={2} />
          </NavBtn>
          <NavBtn label="Next month" onClick={() => setCursor(addMonths(cursor, 1))}>
            <ChevronRight className="h-3.5 w-3.5" strokeWidth={2} />
          </NavBtn>
        </div>
      </header>

      <div className="mb-1 grid grid-cols-7 gap-0.5 px-0.5">
        {WEEKDAY.map((d) => (
          <span
            key={d}
            className="text-center font-mono text-[10px] uppercase tracking-eyebrow text-subtle-foreground"
          >
            {d}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {days.map((d) => {
          const inMonth = d.getMonth() === cursor.getMonth();
          const selected = value !== null && isSameLocalDay(d, value);
          const todayCell = isSameLocalDay(d, today);
          const disabled = minDate !== undefined && d < startOfLocalDay(minDate);
          return (
            <button
              key={d.getTime()}
              type="button"
              disabled={disabled}
              onClick={() => onChange(d)}
              className={cn(
                'relative flex h-9 items-center justify-center rounded-lg font-mono text-[13px] tabular-nums transition duration-fast ease-out-quint',
                'disabled:cursor-not-allowed disabled:opacity-30',
                !disabled && 'hover:bg-surface-muted',
                inMonth ? 'text-foreground' : 'text-subtle-foreground/60',
                selected && 'bg-accent text-primary-foreground hover:bg-accent',
                !selected && todayCell && 'ring-1 ring-accent/50',
              )}
              aria-pressed={selected}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function NavBtn({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="rounded-lg p-1.5 text-muted-foreground transition duration-fast ease-out-quint hover:bg-surface-muted hover:text-foreground"
    >
      {children}
    </button>
  );
}

function startOfMonth(d: Date): Date {
  const out = new Date(d);
  out.setDate(1);
  out.setHours(0, 0, 0, 0);
  return out;
}

function addMonths(d: Date, n: number): Date {
  const out = new Date(d);
  out.setMonth(out.getMonth() + n);
  return out;
}
